import React, { useContext, useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import { FiEye, FiPlus, FiSave, FiTrash } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { useHistory } from 'react-router-dom'

import masks from '../../../../util/masks'
import { Container } from './styles'
import Card from '../../../../components/Card'
import { Button, File, Select, Textarea, Textbox } from '../../../../components/Form'
import { TipContext } from '../../../../contexts/TipContext'
import { loadTips } from '../../../../util/tip'
import Table from '../../../../components/Table'
import { GlobalContext } from '../../../../contexts/GlobalContext'
import { api, authHeaders } from '../../../../services/api'
import { openTabSafari } from '../../../../util/file'
import formUtils from '../../../../util/form'
import { concatFormacoes } from '../../EspacoMedico/DadosCadastrais'

const initialValues = {
	nome: '',
	descricao: '',
	objetivo: '',
	area_atuacao: '',
	pessoa_busca: null,
	instituicao: null,
	empresa: null,
	inicio: '',
	fim: ''
}

export default function () {
	const { user, checkException } = useContext(GlobalContext)
	const { setCodigo, tips } = useContext(TipContext)

	const history = useHistory()

	const [documentos, setDocumentos] = useState([])
	const [empresas, setEmpresas] = useState([])
	const [nenhumaPessoaEncontrada, setNenhumaPessoaEncontrada] = useState(false)
	const [pessoasBusca, setPessoasBusca] = useState(null)
	const [pessoasIncluidas, setPessoasIncluidas] = useState([])
	const [faculdades, setFaculdades] = useState([])
	const [isSearchingPessoa, setIsSearchingPessoa] = useState(false)
	const [funcoes, setFuncoes] = useState(null)
	const [pessoaAIncluir, setPessoaAIncluir] = useState(null)

	async function handleAnexarDocumento(values) {
		setDocumentos(old => [
			...old,
			{
				id: values.arquivo,
				arquivo: values.arquivo,
				descricao: values.documento_descricao
			}
		])
	}

	async function loadEmpresas() {
		const response = await api.get('instituicao')

		setEmpresas(response)
	}

	async function handleCadastrar(values) {
		try {
			values = formUtils.extractFormValues(values)

			values.documentos = documentos
			values.autor = user.pessoa.id
			values.participantes = pessoasIncluidas

			const { projeto_id } = await api.post('projeto', values, authHeaders())

			toast.success('Projeto cadastrado com sucesso!')

			history.push(`/projeto/${projeto_id}`)
		} catch (e) {
			checkException(e)
		}
	}

	async function loadFaculdades() {
		try {
			const faculdades = await api.get('faculdade')

			setFaculdades(faculdades)
		} catch (e) {
			checkException(e)
		}
	}

	async function handleAbrirDocumento(doc) {
		const response = await api.get(`arquivo/${doc.id}`)

		openTabSafari(response.link)
	}

	async function handleRemoverParticipante(part) {
		setPessoasIncluidas(old => old.filter(item => item.id !== part.id))
	}

	async function loadFuncoes() {
		const response = await api.get('funcao', authHeaders())

		setFuncoes(response)
	}

	function handleRemoverDocumento(doc) {
		setDocumentos(old => old.filter(item => item.arquivo !== doc.arquivo))
	}

	const searchPerson = useDebouncedCallback(async function (text) {
		if (!text) {
			setPessoasBusca([])
			return
		}

		try {
			setIsSearchingPessoa(true)

			const response = await api.get('pessoa', {
				params: {
					nome: text
				},
				...authHeaders()
			})

			const resultado = response.filter(item => !pessoasIncluidas.some(p => p.id === item.id))

			setNenhumaPessoaEncontrada(resultado.length === 0)

			setPessoasBusca(resultado.map(pessoa => ({
				id: pessoa.id,
				nome: pessoa.nome,
				formacao: pessoa.formacoes?.reduce((result, item) => {
					return [
						...result,
						`${item.grau.descricao} em ${concatFormacoes(item.formacao)}`
					]
				}, []).join('<br/>')
			})))

			setIsSearchingPessoa(false)
		} catch (e) {
			checkException(e)
		}
	}, 1000)

	useEffect(() => {
		loadTips(setCodigo, 'form_projeto_cadastro')

		loadFaculdades()
		loadEmpresas()
		loadFuncoes()
	}, [])

	if (!funcoes) {
		return null
	}

	return (
		<>
			<Container>
				<Card>
					<h1 className="mb-2">Cadastro de projeto</h1>

					<Formik
						onSubmit={handleCadastrar}
						initialValues={initialValues}
					>
						{({ isSubmitting, errors, setFieldValue, values }) => (
							<Form className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-x-4">
								<p className="m-2 lg:col-span-2">{tips?.formulario}</p>

								<div className="mx-2 font-semibold text-lg mt-4 lg:col-span-2">
									Autor do projeto: {user?.pessoa.nome}
								</div>

								<div>
									<Textbox
										name="nome"
										label="Nome do projeto"
									/>

									<Textbox
										name="area_atuacao"
										label="Área de atuação"
									/>
								</div>

								<Textarea
									name="descricao"
									label="Descrição"
									style={{
										height: 110
									}}
								/>

								<Textarea
									name="objetivo"
									label="Objetivo"
								/>

								<Select
									name="instituicao"
									label="Instituição de ensino"
									onChange={(selected, meta) => { setFieldValue(meta.name, selected) }}
									options={faculdades.map(faculdade => ({
										label: `${faculdade.descricao} ${faculdade.sigla ? `(${faculdade.sigla})` : ''}`,
										value: faculdade.id
									}))}
								/>

								<Textbox
									mask={masks.date}
									name="inicio"
									label="Data de início do projeto"
								/>

								<Textbox
									mask={masks.date}
									name="fim"
									label="Data de fim do projeto"
								/>

								<div className="flex flex-col">
									<Select
										name="empresa"
										label="Empresa investidora"
										onChange={(selected, meta) => {
											setFieldValue(meta.name, selected)
										}}
										options={empresas
											.map(empresa => ({
												label: `${empresa.razao_social} (${empresa.cnpj})`,
												value: empresa.id
											}))
										}
									/>

									<Link
										to="/gerenciar/instituicao"
										className="text-white hover:opacity-80 hover:text-white mx-2 w-fit"
										style={{ fontSize: 11 }}
									>
										Não encontrei a empresa na lista.
									</Link>
								</div>

								<div></div>

								<div className="flex flex-col border-y border-y-[#fff5] pb-4 my-4 lg:col-span-2">
									<h2 className="text-lg font-semibold mx-2 mt-3">Inclusão de participantes</h2>

									<div className="flex flex-col lg:grid lg:grid-cols-3">
										<div className="flex flex-col">
											<Textbox
												name="pessoa_busca"
												label="Participante"
												onChange={e => {
													setFieldValue('pessoa_busca', e.target.value)

													searchPerson(e.target.value)
												}}
											/>

											{isSearchingPessoa ? (
												<div className="italic text-sm mx-2">Buscando...</div>
											) : !!pessoasBusca?.length ? (
												<ul className="mx-2">
													{pessoasBusca.map(pessoa => (
														<li
															key={pessoa.id}
															className="bg-white text-slate-800 hover:bg-slate-200 p-2 first:rounded-t-md last:rounded-b-md cursor-pointer"
															onClick={() => {
																setPessoaAIncluir(pessoa)
																setPessoasBusca(null)
																setFieldValue('pessoa_busca', pessoa.nome)
															}}
														>
															{pessoa.nome}
														</li>
													))}
												</ul>
											) : nenhumaPessoaEncontrada ? (
												<div className='italic mx-2 text-xs mt-2'>Nenhuma pessoa correspondente à busca</div>
											) : null}
										</div>

										<Select
											name="funcao"
											label="Função"
											options={funcoes.map(funcao => ({
												label: funcao.descricao,
												value: funcao.id
											}))}
											onChange={(selected, meta) => {
												setFieldValue(meta.name, selected)
											}}
										/>

										<Button
											className="white lg:w-40"
											style={{ marginTop: 27 }}
											onClick={() => {
												setPessoasIncluidas(old => {
													if (pessoaAIncluir) {
														pessoaAIncluir.funcao_id = values.funcao.value

														return [
															...old,
															pessoaAIncluir
														]
													}

													return old
												})

												setFieldValue('funcao', null)
												setFieldValue('pessoa_busca', '')

												setPessoaAIncluir(null)
											}}
											disabled={!pessoaAIncluir || !values.funcao}
										>
											Incluir participante
										</Button>
									</div>

									<Table
										headers={[
											{ name: 'nome', value: 'Nome' },
											{ name: 'formacao', value: 'Formação' }
										]}
										data={pessoasIncluidas}
										emptyLabel="Nenhum participante incluído"
										actions={[
											{
												action: handleRemoverParticipante,
												icon: FiTrash,
												iconSize: 16,
												name: 'Remover'
											}
										]}
										applyMarginTop
										className="mx-2"
									/>

									<p className="text-sm mt-4 mx-2">Caso deseje incluir participantes a partir de uma planilha, você poderá fazer isto após a etapa de criação do projeto.</p>
								</div>

								<Formik
									onSubmit={handleAnexarDocumento}
									initialValues={{
										arquivo: null,
										documento_descricao: null
									}}
								>
									{({ setFieldValue: setFieldValue2, submitForm }) => (
										<Form className="flex flex-col lg:grid lg:grid-cols-[20rem_20rem_auto] gap-x-4 gap-y-2 lg:col-span-2">
											<h2 className="text-lg font-semibold mx-2 mt-3 col-span-3">Inclusão de documentos</h2>

											<File
												name="arquivo"
												onSuccess={fileId => {
													setFieldValue2('arquivo', fileId)
												}}
												label="Documento"
												format="square"
												previewSize={['100%', '150px']}
												error={errors['arquivo']}
												maxSize={10 * 1024 * 1024}
												className="row-span-2"
											/>

											<Textbox
												name="documento_descricao"
												label="Nome do documento"
											/>

											{!!documentos.length ? (
												<Table
													headers={[
														{ name: 'descricao', value: 'Documento' }
													]}
													data={documentos}
													className="row-span-2"
													actions={[
														{
															action: handleAbrirDocumento,
															icon: FiEye,
															iconSize: 16,
															name: 'Abrir'
														},
														{
															action: handleRemoverDocumento,
															icon: FiTrash,
															iconSize: 16,
															name: 'Remover'
														}
													]}
												/>
											) : <div></div>}

											<Button onClick={submitForm} className="transparent w-fit justify-self-end self-end">
												Incluir documento
												<FiPlus size={16} />
											</Button>
										</Form>
									)}
								</Formik>

								<Button type="submit" className="white self-end justify-self-end w-fit lg:col-start-2" style={{ marginTop: '1.2rem' }} loading={isSubmitting}>
									Cadastrar
									<FiSave size={16} />
								</Button>
							</Form>
						)}
					</Formik>
				</Card>
			</Container>
		</>
	)
}
